/**
 * @author       hugo@zumper.com (Hugo Pineda)
 * @copyright    Copyright (c) 2020, Zumper
 */

import { DEFAULT_FILTERS } from '@zcode-ui/filters'
import { PROP_NAMES } from '@zcode/mixpanel'
import { selectNearbyHoods } from '@zcode/nr/es/shared/modules/nearbyHoods/selectors'
import type { State } from '@zcode/state-type'
import { isNumber, isTruthy } from '@zcode/utils'
import deepequal from 'fast-deep-equal'
import { logger } from '../../../logger'
import { selectFilters } from '../../filters/selectors/index'
import { mapAmenities } from './mapAmenities'
import { mapBedrooms } from './mapBedrooms'
import { mapNeighborhoods } from './mapNeighborhoods'
import { mapOrFeatures } from './mapOrFeatures'
import { mapOtherFilters } from './mapOtherFilters'
import { mapPets } from './mapPets'
import { mapPropertyCategories } from './mapPropertyCategories'

const ratingTextMap: { [key: number]: string } = { 9: 'Excellent', 7: 'Very good', 5: 'Good' }

export const getFilterPropObject = (state: State) => {
  const filters = selectFilters(state)

  if (deepequal(filters, DEFAULT_FILTERS)) {
    return {}
  }

  const {
    bathroomsRange,
    bedrooms,
    buildingAmenities,
    cats,
    dogs,
    floorplans,
    guests,
    longTerm,
    listingAmenities,
    maxPrice,
    minPrice,
    minNightlyPrice,
    maxNightlyPrice,
    neighborhoodIds,
    orFeatures,
    rating,
    propertyTypes,
    shortTerm,
    startDate,
    endDate,
  } = filters

  const filterProps: {
    [PROP_NAMES.ADULTS]?: number
    [PROP_NAMES.AMENITIES_FILTER]?: ReturnType<typeof mapAmenities>
    [PROP_NAMES.BATHROOM_FILTER]?: readonly number[]
    [PROP_NAMES.BEDROOM_FILTER]?: ReturnType<typeof mapBedrooms>
    [PROP_NAMES.BUILDING_FILTER]?: 'Hide buildings' | 'Only buildings'
    [PROP_NAMES.CHILDREN]?: number
    [PROP_NAMES.LEASE_LENGTH_FILTER]?: Array<'Long term' | 'Short term'>
    [PROP_NAMES.MAX_PRICE_FILTER]?: number
    [PROP_NAMES.MIN_PRICE_FILTER]?: number
    [PROP_NAMES.NEIGHBORHOOD_FILTER]?: ReturnType<typeof mapNeighborhoods>
    [PROP_NAMES.OTHER_FILTER]?: string[]
    [PROP_NAMES.SHORT_TERM_END_DATE]?: string
    [PROP_NAMES.SHORT_TERM_START_DATE]?: string
    [PROP_NAMES.PETS_FILTER]?: ReturnType<typeof mapPets>
    [PROP_NAMES.TYPE_FILTER]?: ReturnType<typeof mapPropertyCategories>
    [PROP_NAMES.PROPERTY_FEATURE_FILTER]?: ReturnType<typeof mapOrFeatures>
    [PROP_NAMES.RATING_FILTER]?: string
  } = {}

  try {
    if (shortTerm !== DEFAULT_FILTERS.shortTerm) {
      const leaseLength = (
        [longTerm ? 'Long term' : false, shortTerm ? 'Short term' : false] as const
      ).filter(isTruthy)
      if (leaseLength?.length) {
        filterProps[PROP_NAMES.LEASE_LENGTH_FILTER] = leaseLength
      }
    }

    if (orFeatures?.[0]?.length) {
      const [features] = orFeatures
      filterProps[PROP_NAMES.PROPERTY_FEATURE_FILTER] = mapOrFeatures(features)
    }

    if (!deepequal(bedrooms, DEFAULT_FILTERS.bedrooms)) {
      filterProps[PROP_NAMES.BEDROOM_FILTER] = mapBedrooms(bedrooms)
    }

    if (cats || dogs) {
      filterProps[PROP_NAMES.PETS_FILTER] = mapPets(filters)
    }

    if (floorplans === 0) {
      filterProps[PROP_NAMES.BUILDING_FILTER] = 'Hide buildings'
    } else if (floorplans === 1) {
      filterProps[PROP_NAMES.BUILDING_FILTER] = 'Only buildings'
    }

    if (
      !deepequal(listingAmenities, DEFAULT_FILTERS.listingAmenities) ||
      !deepequal(buildingAmenities, DEFAULT_FILTERS.buildingAmenities)
    ) {
      filterProps[PROP_NAMES.AMENITIES_FILTER] = mapAmenities(listingAmenities, buildingAmenities)
    }

    if (!deepequal(propertyTypes, DEFAULT_FILTERS.propertyTypes)) {
      filterProps[PROP_NAMES.TYPE_FILTER] = mapPropertyCategories(propertyTypes)
    }

    if (
      maxPrice !== DEFAULT_FILTERS.maxPrice ||
      maxNightlyPrice !== DEFAULT_FILTERS.maxNightlyPrice
    ) {
      filterProps[PROP_NAMES.MAX_PRICE_FILTER] = longTerm ? maxPrice : maxNightlyPrice
    }

    if (
      minPrice !== DEFAULT_FILTERS.minPrice ||
      minNightlyPrice !== DEFAULT_FILTERS.minNightlyPrice
    ) {
      filterProps[PROP_NAMES.MIN_PRICE_FILTER] = longTerm ? minPrice : minNightlyPrice
    }

    if (bathroomsRange?.length) {
      filterProps[PROP_NAMES.BATHROOM_FILTER] = bathroomsRange
    }

    if (!deepequal(neighborhoodIds, DEFAULT_FILTERS.neighborhoodIds)) {
      const nearbyHoods = selectNearbyHoods(state)
      if (nearbyHoods?.length) {
        filterProps[PROP_NAMES.NEIGHBORHOOD_FILTER] = mapNeighborhoods(nearbyHoods, neighborhoodIds)
      }
    }

    if (startDate && !longTerm) {
      filterProps[PROP_NAMES.SHORT_TERM_START_DATE] = startDate
    }

    if (endDate && !longTerm) {
      filterProps[PROP_NAMES.SHORT_TERM_END_DATE] = endDate
    }

    if (guests?.length) {
      const [adults, children] = guests
      filterProps[PROP_NAMES?.ADULTS] = adults
      filterProps[PROP_NAMES?.CHILDREN] = children
    }

    const { min } = rating
    if (isNumber(min)) {
      filterProps[PROP_NAMES.RATING_FILTER] = ratingTextMap[min]
    }

    const other = mapOtherFilters(filters)
    if (other?.length) {
      filterProps[PROP_NAMES.OTHER_FILTER] = other
    }
  } catch (e) {
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    logger.error(`Error gathering filter props for mixpanel: ${e?.message}`)
  }

  return filterProps
}
